/**
 * Read/write access for `asset*` events.
 *
 * Deliberately thin. Mutations go through the existing `api/farmEvents` redux thunks rather than a
 * parallel axios client, so retry, tenant resolution and the store stay in one place - this module
 * only builds the payload and names the query.
 *
 * Reads are wired to the real API helper; writes return the payload for a caller to dispatch.
 *
 * **`from` and `to` are unix seconds**, not ISO. The envelope's `time` is ISO-8601 and the query
 * parameters are seconds - `validateEventsQuery` parses them with `parseInt` and rejects anything
 * negative. `asset-projection/period.js` converts, so callers working in dates should go through it
 * rather than doing the arithmetic at each call site.
 */

import { fetchEventsQuery } from 'api/farmEvents'
import { ASSET_EVENT_TYPE_LIST, LOCATION_EVENT_TYPES, assetEventTypeDef, editableEventPaths, isAssetEventType, isReportReadableEventType } from './asset-event-types'
import { buildAssetEvent } from './build-asset-event'

/**
 * Fetch the register history for one asset, newest first.
 *
 * Note the API's default page is 100 with no cursor, so `maxResults` is explicit here. A long-lived
 * asset will exceed any ceiling eventually; real pagination is blocked on the API offering a cursor
 * (the same limitation `ANIMAL_HISTORY_MAX_RESULTS` works around for livestock).
 */
export const fetchAssetEvents = ({ tenant, assetId, types, from, to, maxResults = 1000 } = {}) => {
    if (assetId == null) throw new Error('fetchAssetEvents: assetId is required')
    const wanted = Array.isArray(types) && types.length ? types.filter(isAssetEventType) : ASSET_EVENT_TYPE_LIST
    return fetchEventsQuery({
        tenant,
        query: {
            assetIds: String(assetId),
            types: wanted.join(','),
            order: 'desc',
            maxResults,
            ...(from != null ? { from } : {}),
            ...(to != null ? { to } : {}),
        },
    })
}

/**
 * Fetch register history for **many** assets in one call - what a report needs.
 *
 * `assetIds` is a comma list to the API and the events come back interleaved, so the caller groups
 * them. Ordered ascending here rather than descending: a replay applies events oldest-first, and a
 * report reads a period in the order it happened.
 *
 * The `maxResults` ceiling is the same unsolved problem `fetchAssetEvents` documents - the API
 * offers no cursor, so a ceiling moves the truncation point rather than removing it. This returns
 * the rows and nothing else; deciding whether a result was truncated belongs to the caller that
 * knows what it asked for. `asset-projection/assemble-snapshot.js` does that check and says so on
 * the artefact.
 */
export const fetchAssetEventsForAssets = ({ tenant, assetIds, types, from, to, maxResults = 5000 } = {}) => {
    const subjects = (Array.isArray(assetIds) ? assetIds : assetIds == null ? [] : [assetIds]).filter(id => id != null && id !== '')
    // Any declared ledger type, not only `asset*`: a report replaying the `chemStore*` series files it
    // against the shed, so it is read here with everything else. Livestock's treatment and movement
    // types too (`REPORT_READ_EVENT_TYPES`) - the LPA sections read them. The default stays `asset*` only.
    const wanted = Array.isArray(types) && types.length ? types.filter(isReportReadableEventType) : ASSET_EVENT_TYPE_LIST
    return fetchEventsQuery({
        tenant,
        query: {
            // Craig, 10 Sep 2026: *"if you drop the assetIds= part, it does all assets"*.
            //
            // Omitting it beats listing every id, and not only for the URL. The register holds every
            // asset for the tenant in redux, so the list was the whole tenant written out - 123 ids
            // today and a query string that grows with the farm. It also required at least one id,
            // which is why an empty register could not read the ledger at all.
            //
            // The trade-off, stated because it is real: without the filter the read also returns
            // events whose subject the register does not list, so `SNAPSHOT_MAX_EVENTS` is reached
            // sooner. The snapshot already detects truncation and says so on the artefact, which is
            // the honest handling either way.
            ...(subjects.length ? { assetIds: subjects.join(',') } : {}),
            // `types` is kept, deliberately. Craig, 10 Sep 2026: dropping it returns every type and
            // you filter after. That would pull livestock, chemical and every other module's events
            // into a read this module then discards - and they would count against `maxResults`
            // first, so the ceiling would be reached by events nobody asked for. Filtering at the
            // server is what keeps the truncation point as far away as possible.
            types: wanted.join(','),
            order: 'asc',
            maxResults,
            ...(from != null ? { from } : {}),
            ...(to != null ? { to } : {}),
        },
    })
}

/** Location-resolution inputs only - the `assetMove` / `assetContain` slice the A7 resolver reads. */
export const fetchAssetLocationEvents = ({ tenant, assetId, from, to, maxResults } = {}) => fetchAssetEvents({ tenant, assetId, types: LOCATION_EVENT_TYPES, from, to, maxResults })

/**
 * Build a create payload. Returns the body; the caller dispatches
 * `createEvent({ data, tenant })`.
 *
 * Split this way on purpose: the payload is pure and testable, and an offline queue can hold it
 * without holding a dispatch.
 */
export const buildAssetEventCreate = input => buildAssetEvent(input)

/**
 * The API rejects a `detail` larger than this. Checked here so the caller gets a clear message.
 * Source: `internal/services/entity_events_v2.go`.
 */
export const DETAIL_MAX_BYTES = 20480

/** Leaf paths under `value`, with arrays as leaves - an attachment list is one fact, not one per id. */
const leafPaths = (value, prefix) => {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) return [prefix]
    const keys = Object.keys(value)
    return keys.length ? keys.flatMap(key => leafPaths(value[key], `${prefix}.${key}`)) : [prefix]
}

const valueAt = (root, path) => path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), root)

const ENVELOPE_KEYS = ['type', 'time', 'farmIds', 'assetIds', 'paddockIds', 'userIds', 'entityEventIds']

/**
 * What an in-place edit of an `asset*` event changes that its type does not allow.
 *
 * Craig, 17 Sep 2026: free text and attachments are edited in place; anything a figure reads is
 * corrected by a reversal. So every envelope field is refused, and within `detail` only the paths
 * `editableEventPaths` names may differ.
 */
export const disallowedEventChanges = (original, body) => {
    if (!isAssetEventType(original?.type)) return []
    const allowed = editableEventPaths(original.type)
    const envelope = ENVELOPE_KEYS.filter(key => JSON.stringify(original?.[key] ?? null) !== JSON.stringify(body?.[key] ?? null))
    const paths = [...new Set([...leafPaths({ detail: original?.detail || {} }, '').map(p => p.slice(1)), ...leafPaths({ detail: body?.detail || {} }, '').map(p => p.slice(1))])]
    const detail = paths.filter(path => JSON.stringify(valueAt(original, path) ?? null) !== JSON.stringify(valueAt(body, path) ?? null)).filter(path => !allowed.some(ok => path === ok || path.startsWith(`${ok}.`)))
    return [...envelope, ...detail]
}

/**
 * Build an update payload for `PUT /events/:id`.
 *
 * The update is a **full replace**. Send the whole event, not a patch. Fields you omit are cleared.
 *
 * The API validates four things. `type` must be non-empty. `time` must be non-zero. `farmIds` must
 * be present and permitted. `detail` must be 20480 bytes or fewer. This function checks the detail
 * size, because the API returns a validation error without a reason.
 *
 * **For an `asset*` event only free text and attachments may change** (`editableEventPaths`). A
 * change to anything a figure reads throws, naming the keys, and the correction is
 * `buildAssetEventReversal` plus a new event. Other ledger families keep their own rules.
 *
 * @param {Object} original the event as it stands, including its id
 * @param {Object} changes top-level fields to replace, and `detail` to merge
 */
export const buildAssetEventUpdate = (original, changes = {}) => {
    if (!original?.id) throw new Error('buildAssetEventUpdate: the original event must have an id')

    const { detail: detailChanges, ...rootChanges } = changes
    const detail = { ...(original.detail || {}), ...(detailChanges || {}) }

    const body = {
        id: original.id,
        type: original.type,
        time: original.time,
        farmIds: original.farmIds,
        assetIds: original.assetIds,
        ...(original.paddockIds ? { paddockIds: original.paddockIds } : {}),
        ...(original.userIds ? { userIds: original.userIds } : {}),
        ...(original.entityEventIds ? { entityEventIds: original.entityEventIds } : {}),
        ...rootChanges,
        detail,
    }

    const refused = disallowedEventChanges(original, body)
    if (refused.length) {
        throw new Error(`buildAssetEventUpdate: ${refused.join(', ')} cannot be edited in place on ${original.type} - reverse the event and record it again`)
    }

    if (JSON.stringify(body.detail).length > DETAIL_MAX_BYTES) {
        throw new Error(`buildAssetEventUpdate: detail is over the API limit of ${DETAIL_MAX_BYTES} bytes`)
    }
    return body
}

/**
 * Build the create body that reverses `original` (`AR-MOD-02`, `AR-EVT-03`).
 *
 * - **Same `type`**, so a read filtered by type fetches the reversal with the event it undoes.
 * - **Same `time`**, so a read of the original's period fetches both. When the correction was made is
 *   `detail.ledger.recordedAt`, and `backdated` says the two differ.
 * - **No branch.** A reversal states no fact of its own; `withoutReversed` drops the pair.
 * - The original is left exactly as it is.
 *
 * @param {Object} original the stored event, with its id
 * @param {{ reason: string, recordedAt: string, source: string, actor?: Object }} input
 */
export const buildAssetEventReversal = (original, { reason, recordedAt, source, actor } = {}) => {
    if (!original?.id) throw new Error('buildAssetEventReversal: the original event must have an id')
    const def = assetEventTypeDef(original.type)
    if (!def) throw new Error(`buildAssetEventReversal: "${original.type}" is not an asset-register event type`)
    if (original?.detail?.ledger?.reversesEventId != null) throw new Error('buildAssetEventReversal: a reversal is not itself reversed - record the fact again instead')
    if (!String(reason || '').trim()) throw new Error('buildAssetEventReversal: a reason is required')

    const [assetId, ...assetIds] = original.assetIds || []
    const event = buildAssetEvent({
        type: original.type,
        assetId,
        assetIds,
        farmIds: original.farmIds,
        time: original.time,
        recordedAt,
        source,
        actor,
        subtype: original?.detail?.subtype,
        paddockIds: original.paddockIds,
        ledger: { reason: String(reason).trim(), reversesEventId: original.id },
    })
    delete event.detail[def.branch]
    return event
}

/** Delete an event by id. The backend soft-deletes, so `GET` stops returning it. */
export const buildAssetEventDelete = original => {
    if (!original?.id) throw new Error('buildAssetEventDelete: the original event must have an id')
    return { id: original.id }
}

export default { fetchAssetEvents, fetchAssetEventsForAssets, fetchAssetLocationEvents, buildAssetEventCreate, buildAssetEventUpdate, buildAssetEventReversal, buildAssetEventDelete }
